import {Checkbox, CheckboxProps, IconButton, Separator} from 'platform/components';
import {
  Box,
  Clickable,
  getColorByPath,
  HStack,
  Show,
  Space,
  Text,
  ThemeColorPath,
} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {isNotNil, padCharsStart} from 'ramda-adjunct';

import {SourcingVehicleListItemResponseBody} from '@dms/api/core';
import {testIds} from '@dms/routes';

import {useVehicleDoubleClick} from '../../../hooks/useVehicleDoubleClick';
import {Row} from '../../../types/Row';
import {getVehicleSellerCountry} from '../../../utils/getVehicleSellerCountry';
import {isAuctionVehicle} from '../../../utils/isAuctionVehicle';
import {isClassifiedsAdInactive} from '../../../utils/isClassifiedsAdInactive';
import {CountryFlag} from '../../CountryFlag/CountryFlag';
import {COMPACT_TABLE_GEOMETRY} from '../constants/COMPACT_TABLE_GEOMETRY';
import {createRowElementId} from '../utils/createRowElementId';
import {Column} from '../utils/getSourcingTableColumns';
import {TableDetailRow} from './TableDetailRow';

interface TableRowProps {
  hasUnifiedSurfaces: boolean;
  columns: Column[];
  row: Row;
  index: number;
  onClick?: () => void;
  isSelected: boolean;
  isChecked: boolean;
  isExpanded: boolean;
  onExpandedChange: (row: Row, isExpanded: boolean) => void;
  onCheck: (checked: boolean) => void;
  setLastVisitedVehicle: (adId: string, index: number) => void;
}

export function SourcingTableRow(props: TableRowProps) {
  const onDoubleClick = useVehicleDoubleClick(props.row.sourcingVehicle.adId, () =>
    props.setLastVisitedVehicle(props.row.sourcingVehicle.adId, props.index)
  );
  const indexFrom1 = props.index + 1;
  const sellerCountry = getVehicleSellerCountry(props.row.sourcingVehicle);
  const isDisabled =
    !isAuctionVehicle(props.row) && isClassifiedsAdInactive(props.row.sourcingVehicle);

  const backgroundColor = match<
    {isSelected: boolean; isNew?: boolean; isExpanded: boolean; isDisabled: boolean},
    ThemeColorPath | undefined
  >({
    isSelected: props.isSelected,
    isNew: props.row.isNew,
    isExpanded: props.isExpanded,
    isDisabled,
  })
    .with({isSelected: true}, () => 'palettes.blue.20.100')
    .with({isNew: true}, () => 'palettes.green.20.100')
    .with({isDisabled: true}, () => 'palettes.neutral.30.100')
    .with({isExpanded: true}, () => 'palettes.neutral.10.100')
    .otherwise(() => undefined);

  const hoverBackgroundColor = match<
    {isSelected: boolean; isNew?: boolean; isDisabled: boolean},
    ThemeColorPath
  >({isSelected: props.isSelected, isNew: props.row.isNew, isDisabled})
    .with({isSelected: true}, () => 'palettes.blue.30.100')
    .with({isNew: true}, () => 'palettes.green.30.100')
    .with({isDisabled: true}, () => 'palettes.neutral.40.100')
    .otherwise(() => 'palettes.blue.10.100');

  const leftBorderColor = match<SourcingVehicleListItemResponseBody, ThemeColorPath | undefined>(
    props.row
  )
    .with({inCart: true}, () => 'palettes.blue.60.100')
    .with({favourite: true}, () => 'palettes.orange.50.100')
    .with({comparison: true}, () => 'palettes.purple.60.100')
    .with({seen: true}, () => 'palettes.neutral.40.100')
    .otherwise(() => undefined);

  const onCheckboxChange: CheckboxProps['onChange'] = (value) => {
    props.onCheck(value);
  };

  return (
    <Clickable onClick={props.onClick}>
      <RowSurface
        $backgroundColor={backgroundColor}
        $hoverBackgroundColor={hoverBackgroundColor}
        $enhancedSurfaces={props.hasUnifiedSurfaces}
        onDoubleClick={onDoubleClick}
      >
        <Box
          id={createRowElementId(props.row)}
          paddingVertical={2}
          paddingHorizontal={COMPACT_TABLE_GEOMETRY.horizontalPadding}
          position="relative"
        >
          <Box
            position="absolute"
            left={0}
            top={0}
            bottom={0}
            width={1}
            backgroundColor={leftBorderColor}
          />
          <HStack spacing={COMPACT_TABLE_GEOMETRY.columnSpacing}>
            <HStack
              width={COMPACT_TABLE_GEOMETRY.checkboxColumnWidth}
              spacing={COMPACT_TABLE_GEOMETRY.columnSpacing}
            >
              <Clickable onClick={(event) => event.stopPropagation()}>
                <Checkbox
                  value={props.isChecked}
                  onChange={onCheckboxChange}
                  data-testid={testIds.sourcing.classifieds('sourcingTable-checkbox')}
                />
              </Clickable>
              <Box>
                <Text size="xxSmall" color={isDisabled ? 'tertiary' : 'secondary'}>
                  {padCharsStart('0', 4, indexFrom1.toString())}
                </Text>
                <Space vertical={1} />
                <Show when={sellerCountry}>
                  <CountryFlag country={sellerCountry} />
                </Show>
              </Box>
            </HStack>
            {props.columns.map((column) => (
              <Box
                key={column.id}
                width={column.width}
                flex={isNotNil(column.width) ? undefined : 1}
              >
                <column.Cell row={{original: props.row}} />
              </Box>
            ))}
            <Box width={COMPACT_TABLE_GEOMETRY.trailingSpaceWidth}>
              <IconButton
                icon={
                  props.isExpanded ? 'hardware/keyboard_arrow_up' : 'hardware/keyboard_arrow_down'
                }
                onClick={(event) => {
                  event.stopPropagation();
                  props.onExpandedChange(props.row, !props.isExpanded);
                }}
                size="small"
                data-testid={testIds.sourcing.classifieds('sourcingTable-iconButton')}
              />
            </Box>
          </HStack>
          <Show when={props.isExpanded}>
            <Box paddingLeft={16}>
              <Separator orientation="horizontal" spacing={2} />
              <TableDetailRow
                row={{
                  index: props.index,
                  original: props.row,
                }}
                setLastVisitedVehicle={props.setLastVisitedVehicle}
                isSelected={props.isSelected}
                data-testid={testIds.sourcing.classifieds('sourcingTable-detailRow')}
              />
            </Box>
          </Show>
        </Box>
      </RowSurface>
    </Clickable>
  );
}

const RowSurface = styled.div<{
  $backgroundColor?: ThemeColorPath;
  $hoverBackgroundColor: ThemeColorPath;
  $enhancedSurfaces: boolean;
}>`
  background-color: ${({$backgroundColor, theme}) => getColorByPath($backgroundColor, theme)};
  ${({$enhancedSurfaces, $hoverBackgroundColor, theme}) =>
    $enhancedSurfaces &&
    `
      outline: 1px solid transparent;
      outline-offset: -1px;
      transition: background-color 150ms ease, outline-color 150ms ease;
      @media (hover: hover) {
        &:hover {
          background-color: ${getColorByPath($hoverBackgroundColor, theme)};
          outline-color: ${theme.colors.palettes.blue[40][100]};
        }
      }
      @media (prefers-reduced-motion: reduce) { transition: none; }
    `}
`;
